import { useState } from 'react'
import type { TypeCode } from '@mindtype/shared'
import { getEnvironmentMatch } from '@mindtype/shared'

interface ColorConfig {
  primary: string
  secondary: string
  text: string
}

interface CareerDevelopmentModuleProps {
  typeCode: TypeCode
  colorConfig?: ColorConfig
}

const DEFAULT_COLOR = { primary: '#3b82f6', secondary: '#60a5fa', text: '#ffffff' }

// 环境特性维度定义
interface EnvironmentDimension {
  id: string
  name: string
  leftLabel: string
  rightLabel: string
}

const MAIN_DIMENSIONS: EnvironmentDimension[] = [
  {
    id: 'structure',
    name: '结构确定性',
    leftLabel: '流动探索',
    rightLabel: '稳定清晰',
  },
  {
    id: 'evaluation',
    name: '评价外显度',
    leftLabel: '内隐延迟',
    rightLabel: '外显即时',
  },
  {
    id: 'autonomy',
    name: '自主决策度',
    leftLabel: '执行导向',
    rightLabel: '自主导向',
  },
]

// ============ 倾向位置计算 ============
// 根据16种类型速查表精确映射位置值

type StructureLevel = 'stable' | 'middle' | 'fluid'
type EvaluationLevel = 'implicit' | 'middleImplicit' | 'middleExplicit' | 'explicit'
type AutonomyLevel = 'autonomous' | 'middleAutonomous' | 'middleExecution' | 'adaptExecution'

function getStructureLevel(d2: string, d4: string): StructureLevel {
  if (d2 === 'S' && d4 === 'D') return 'stable'
  if (d2 === 'C' && d4 === 'O') return 'fluid'
  return 'middle'
}

function getEvaluationLevel(d1: string, d3: string): EvaluationLevel {
  if (d1 === 'A' && d3 === 'I') return 'implicit'
  if (d1 === 'A' && d3 === 'E') return 'middleImplicit'
  if (d1 === 'R' && d3 === 'I') return 'middleExplicit'
  return 'explicit' // R + E
}

function getAutonomyLevel(d1: string, d3: string): AutonomyLevel {
  if (d1 === 'A' && d3 === 'I') return 'autonomous'
  if (d1 === 'A' && d3 === 'E') return 'middleAutonomous'
  if (d1 === 'R' && d3 === 'I') return 'middleExecution'
  return 'adaptExecution' // R + E
}

// 将级别转换为滑块位置 (0-100)
function getEnvironmentPreferences(typeCode: string) {
  const [d1, d2, d3, d4] = typeCode.split('')

  const structureLevel = getStructureLevel(d2, d4)
  const evaluationLevel = getEvaluationLevel(d1, d3)
  const autonomyLevel = getAutonomyLevel(d1, d3)

  // 结构确定性位置
  const structureMap: Record<StructureLevel, number> = {
    stable: 80,
    middle: 50,
    fluid: 20,
  }

  // 评价外显度位置
  const evaluationMap: Record<EvaluationLevel, number> = {
    implicit: 20,
    middleImplicit: 35,
    middleExplicit: 65,
    explicit: 80,
  }

  // 自主决策度位置
  const autonomyMap: Record<AutonomyLevel, number> = {
    autonomous: 80,
    middleAutonomous: 65,
    middleExecution: 35,
    adaptExecution: 20,
  }

  return {
    structure: structureMap[structureLevel],
    evaluation: evaluationMap[evaluationLevel],
    autonomy: autonomyMap[autonomyLevel],
  }
}

// ============ 主组件 ============

export function CareerDevelopmentModule({
  typeCode,
  colorConfig = DEFAULT_COLOR,
}: CareerDevelopmentModuleProps) {
  const color = colorConfig
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [showAuxiliary, setShowAuxiliary] = useState(false)

  const prefs = getEnvironmentPreferences(typeCode)
  const envMatch = getEnvironmentMatch(typeCode)

  // 统一的卡片样式
  const cardStyle = {
    border: `1px solid ${color.primary}15`,
  }

  const cardHeaderStyle = {
    borderBottom: `1px solid ${color.primary}15`,
    backgroundColor: `${color.primary}05`,
  }

  // 将 comfortable 对象转换为数组用于渲染
  const comfortableItems = [
    { title: '结构确定性', description: envMatch.comfortable.structureCertainty },
    { title: '评价外显度', description: envMatch.comfortable.evaluationVisibility },
    { title: '自主决策度', description: envMatch.comfortable.autonomyLevel },
  ]

  return (
    <div className="space-y-6">
      {/* ===== 1. 板块说明 ===== */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: `${color.primary}03`, border: `1px solid ${color.primary}10` }}
      >
        <button
          onClick={() => setShowDisclaimer(!showDisclaimer)}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <div>
            <h3 className="text-sm font-medium text-gray-700">关于这部分内容</h3>
            <p className="text-xs text-gray-500 mt-1">了解如何理解和使用环境匹配分析</p>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showDisclaimer ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showDisclaimer && (
          <div className="px-6 pb-6 space-y-4">
            <div
              className="p-4 rounded-lg"
              style={{ backgroundColor: `${color.primary}05`, border: `1px solid ${color.primary}10` }}
            >
              <h4 className="text-sm font-medium text-gray-800 mb-2">免责声明</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>认知倾向不等于能力水平，也不等于专业技能。</li>
                <li>这里描述的是「什么样的工作环境可能让你感到自在」，而不是「你应该从事什么职业」。同一个职业在不同公司、不同团队的实际环境差异很大。</li>
                <li>「需要适应」不意味着不能做。很多人在需要适应的环境中，通过学习和刻意练习，同样取得了很好的发展。</li>
                <li className="font-medium" style={{ color: color.primary }}>请把以下内容作为自我观察的线索，而非决策的依据。</li>
              </ul>
            </div>

            <div
              className="p-4 rounded-lg"
              style={{ backgroundColor: `${color.primary}05`, border: `1px solid ${color.primary}10` }}
            >
              <h4 className="text-sm font-medium text-gray-800 mb-2">使用指南</h4>
              <ol className="space-y-3 text-sm text-gray-600">
                <li>
                  <span className="font-medium text-gray-700">1. 对照你的实际经历</span>
                  <p className="mt-1">回想过去让你感到顺畅或消耗的工作/学习环境，看看是否与描述吻合。</p>
                </li>
                <li>
                  <span className="font-medium text-gray-700">2. 关注「适应成本」而非「能不能」</span>
                  <p className="mt-1">描述中提到的「可能需要适应」不是说你做不了，而是说可能需要付出更多能量。</p>
                </li>
                <li>
                  <span className="font-medium text-gray-700">3. 环境会变，你也会变</span>
                  <p className="mt-1">这份分析基于你当前的测试结果。随着经历积累，你的倾向可能发生变化。</p>
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* ===== 2. 环境匹配分析 ===== */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
        <div className="px-6 py-4" style={cardHeaderStyle}>
          <h2 className="text-lg font-semibold" style={{ color: color.primary }}>环境匹配分析</h2>
          <p className="text-sm text-gray-500 mt-1">基于三个主要环境特性维度</p>
        </div>
        <div className="p-6 space-y-6">
          {/* 环境特征图示 */}
          <div className="space-y-4">
            {MAIN_DIMENSIONS.map((dim) => {
              const value = dim.id === 'structure' ? prefs.structure
                : dim.id === 'evaluation' ? prefs.evaluation
                : prefs.autonomy

              return (
                <div key={dim.id} className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{dim.leftLabel}</span>
                    <span className="font-medium text-gray-700">{dim.name}</span>
                    <span>{dim.rightLabel}</span>
                  </div>
                  <div className="relative h-2 rounded-full bg-gray-100">
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full shadow-md"
                      style={{
                        left: `calc(${value}% - 8px)`,
                        backgroundColor: color.primary,
                      }}
                    />
                    <div
                      className="absolute top-0 h-full rounded-full opacity-20"
                      style={{
                        left: 0,
                        width: `${value}%`,
                        backgroundColor: color.primary,
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          {/* 自在的环境特征 */}
          <div>
            <h3 className="text-base font-semibold mb-4" style={{ color: color.primary }}>
              你可能感到自在的环境特征
            </h3>
            <div className="space-y-4">
              {comfortableItems.map((item, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: `${color.primary}05`, border: `1px solid ${color.primary}15` }}
                >
                  <h4 className="text-sm font-medium mb-2" style={{ color: color.primary }}>{item.title}</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 需要适应的环境特征 */}
          <div>
            <h3 className="text-base font-semibold mb-4 text-gray-700">
              你可能需要适应的环境特征
            </h3>
            <div
              className="p-4 rounded-lg space-y-3"
              style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}
            >
              {envMatch.challenging.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-gray-400 mt-0.5">•</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ===== 3. 辅助信息 ===== */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'white', ...cardStyle }}
      >
        <button
          onClick={() => setShowAuxiliary(!showAuxiliary)}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
          style={cardHeaderStyle}
        >
          <div>
            <h3 className="text-base font-semibold" style={{ color: color.primary }}>辅助信息</h3>
            <p className="text-sm text-gray-500 mt-1">协作依赖度、成果周期、工作对象</p>
          </div>
          <svg
            className={`w-5 h-5 transition-transform ${showAuxiliary ? 'rotate-180' : ''}`}
            style={{ color: color.primary }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAuxiliary && (
          <div className="px-6 pb-6 pt-4 space-y-4">
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">协作依赖度</h4>
                <p className="text-sm text-gray-600">{envMatch.supplementary.collaborationLevel}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">成果周期</h4>
                <p className="text-sm text-gray-600">{envMatch.supplementary.resultCycle}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">工作对象</h4>
                <p className="text-sm text-gray-600">{envMatch.supplementary.workObject}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== 4. 自检问题 ===== */}
      <div
        className="rounded-xl p-6"
        style={{ backgroundColor: `${color.primary}03`, border: `1px solid ${color.primary}10` }}
      >
        <h3 className="text-base font-semibold mb-4" style={{ color: color.primary }}>自检问题</h3>
        <p className="text-sm text-gray-500 mb-4">
          对照你的实际经历思考以下问题：
        </p>
        <div className="space-y-3">
          {envMatch.selfCheckQuestions.map((q, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-lg bg-white"
              style={{ border: `1px solid ${color.primary}10` }}
            >
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                style={{ backgroundColor: `${color.primary}15`, color: color.primary }}
              >
                {i + 1}
              </span>
              <span className="text-sm text-gray-700">{q}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
